import { useSession } from '../App';
import { Link } from '../router';
import { Card, ErrorNotice, Loading, Notice, PageHeader, PaymentBadge, StatusBadge, useAsync } from '../components/ui';
import { daysUntil, formatBytes, formatDate, formatRelative } from '../format';

export function OverviewPage() {
  const { client, claims } = useSession();
  const state = useAsync(
    () => Promise.all([client.me(), client.billing(), client.usage(), client.devices()]),
    [client],
  );

  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorNotice error={state.error} onRetry={() => void state.reload()} />;
  const [me, billing, usage, devices] = state.data!;

  const firstName = (me.displayName ?? claims?.name ?? '').split(' ')[0];
  const subscription = billing.subscription;
  const planName = billing.prices.find((price) => price.plan === subscription.plan)?.planName ?? capitalize(subscription.plan);
  const daysLeft = daysUntil(subscription.currentPeriodEnd);
  const endingSoon = subscription.renewal === 'manual' && daysLeft !== null && daysLeft <= 7;
  const storageQuotaGb = typeof me.features.cloud_storage_gb === 'number' ? me.features.cloud_storage_gb : 0;
  const storagePercent = storageQuotaGb > 0 ? Math.min(100, (usage.storageBytes / (storageQuotaGb * 1024 ** 3)) * 100) : 0;
  const devicePercent = devices.limit ? Math.min(100, (devices.used / devices.limit) * 100) : 0;
  const lastSeenDevice = [...devices.devices].sort((a, b) => b.lastSeenAt.localeCompare(a.lastSeenAt))[0];

  return (
    <>
      <PageHeader
        eyebrow="Overview"
        title={firstName ? `Hello, ${firstName}` : 'Hello'}
        description="Everything about your DeskTouch account at a glance."
      />

      {endingSoon && (
        <Notice tone="warning">
          <strong>{planName} ends {daysLeft! <= 0 ? 'today' : `in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`}.</strong>{' '}
          It does not renew by itself — <Link to="/billing">extend it now</Link> to keep your features.
        </Notice>
      )}

      <div className="grid overview">
        <Card className="plan-card" title="Your plan" aside={subscription.status === 'none' ? undefined : <StatusBadge status={subscription.status} />}>
          <p className="plan-name">{planName}</p>
          <p className="muted">
            {subscription.plan === 'free' || subscription.status === 'none'
              ? 'Remote control on up to two devices, free forever.'
              : subscription.currentPeriodEnd
                ? `${subscription.renewal === 'manual' ? 'Paid through' : 'Active until'} ${formatDate(subscription.currentPeriodEnd)}`
                : 'No end date'}
          </p>
          <Link to="/billing" className="button primary">
            {subscription.plan === 'free' ? 'Upgrade' : 'Manage plan'}
          </Link>
        </Card>

        <Card title="Devices">
          <p className="metric">
            {devices.used}
            <span> / {devices.limit ?? '∞'}</span>
          </p>
          {devices.limit ? <Meter percent={devicePercent} /> : null}
          <p className="muted">
            {lastSeenDevice
              ? `${lastSeenDevice.name} was active ${formatRelative(lastSeenDevice.lastSeenAt)}.`
              : 'No devices yet. Sign in to the DeskTouch app to add one.'}
          </p>
          <Link to="/devices">Manage devices →</Link>
        </Card>

        <Card title="Cloud storage">
          {storageQuotaGb > 0 ? (
            <>
              <p className="metric">
                {formatBytes(usage.storageBytes)}
                <span> / {storageQuotaGb} GB</span>
              </p>
              <Meter percent={storagePercent} />
              <p className="muted">
                {usage.documentCount} synced item{usage.documentCount === 1 ? '' : 's'}.
              </p>
            </>
          ) : (
            <>
              <p className="metric muted-metric">Not included</p>
              <p className="muted">Cloud sync of layouts and settings comes with Pro and Business.</p>
              <Link to="/billing">See plans →</Link>
            </>
          )}
        </Card>

        <Card title="Account">
          <dl className="facts">
            <dt>Email</dt>
            <dd>{me.email ?? claims?.email ?? '—'}</dd>
            <dt>Member since</dt>
            <dd>{formatDate(me.createdAt)}</dd>
            <dt>Last sign-in</dt>
            <dd>{formatRelative(me.lastLoginAt)}</dd>
          </dl>
          <Link to="/profile">Edit profile →</Link>
        </Card>
      </div>

      {billing.payments.length > 0 && (
        <Card title="Latest payment" aside={<Link to="/billing">All payments →</Link>}>
          <p>
            {billing.payments[0]!.description} · {formatDate(billing.payments[0]!.createdAt)} ·{' '}
            <PaymentBadge status={billing.payments[0]!.status} />
          </p>
        </Card>
      )}
    </>
  );
}

function Meter({ percent }: { percent: number }) {
  const tone = percent >= 90 ? 'danger' : percent >= 75 ? 'warning' : '';
  return (
    <div className={`meter ${tone}`} role="meter" aria-valuenow={Math.round(percent)} aria-valuemin={0} aria-valuemax={100}>
      <span style={{ width: `${Math.max(percent, 2)}%` }} />
    </div>
  );
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
